<template>
  <div class="electricity-meter-container">
    <main-navigation />

    <div class="meter-content">
      <!-- HEADER SECTION -->
      <div class="page-header">
        <div>
          <h1 class="page-title">
            <i class="bi bi-lightning-charge"></i>
            Electricity Meter Readings
          </h1>
          <p class="page-subtitle" v-if="houseName">
            {{ houseName }} - Electricity Meter Management
          </p>
        </div>
        <div class="header-actions">
          <button class="btn btn-outline-secondary" @click="goBack">
            <i class="bi bi-arrow-left"></i> Back
          </button>
        </div>
      </div>

      <!-- PROPERTY INFO CARD (using data from units) -->
      <div v-if="units.length" class="property-info-card">
        <div class="property-icon">
          <i class="bi bi-building"></i>
        </div>
        <div class="property-details">
          <h2>{{ houseName }}</h2>
          <p class="property-location" v-if="houseLocation">
            <i class="bi bi-geo-alt"></i>
            {{ houseLocation }}
          </p>
          <div class="property-stats">
            <span class="stat-badge">
              <i class="bi bi-house-door"></i>
              {{ summary.total }} Units
            </span>
            <span class="stat-badge">
              <i class="bi bi-people"></i>
              {{ summary.occupied }} Tenants
            </span>
            <span class="stat-badge" v-if="lastReadingDate">
              <i class="bi bi-calendar-check"></i>
              Last Reading: {{ lastReadingDate }}
            </span>
          </div>
        </div>
      </div>

      <!-- STEP INDICATOR -->
      <div class="steps-container">
        <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
          <div class="step-number">1</div>
          <div class="step-label">Download CSV Template</div>
        </div>
        <div class="step-connector"></div>
        <div class="step" :class="{ active: currentStep === 2 }">
          <div class="step-number">2</div>
          <div class="step-label">Upload Readings CSV</div>
        </div>
      </div>

      <!-- LOADING STATE -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;"></div>
        <p class="mt-3">Loading units data...</p>
      </div>

      <!-- ERROR STATE -->
      <div v-else-if="error" class="error-state">
        <i class="bi bi-exclamation-triangle"></i>
        <h4>Error Loading Data</h4>
        <p>{{ error }}</p>
        <button class="btn btn-primary mt-3" @click="fetchUnits">
          <i class="bi bi-arrow-clockwise"></i> Try Again
        </button>
      </div>

      <!-- STEP 1: DOWNLOAD TEMPLATE -->
      <div v-else-if="currentStep === 1" class="step-card">
        <div class="card-header">
          <i class="bi bi-file-earmark-spreadsheet"></i>
          <h3>Download Meter Reading Template (CSV)</h3>
        </div>

        <div class="template-info">
          <div class="info-icon">
            <i class="bi bi-info-circle-fill"></i>
          </div>
          <div class="info-text">
            <strong>Template contains:</strong> All units in {{ houseName }} with their current tenants and previous electricity readings. 
            Fill in the <span class="highlight">current_reading</span> column with the new  readings.
          </div>
        </div>

        <!-- Template Preview -->
        <div class="template-preview">
          <h5>CSV Format Preview:</h5>
          <div class="preview-csv">
            <pre class="csv-content">{{ csvPreviewContent }}</pre>
          </div>
          <p class="preview-note">
            <i class="bi bi-info-circle"></i>
            The CSV file will have these columns: unit_number, tenant_name, previous_reading, current_reading
          </p>
        </div>

        <!-- Download Button -->
        <div class="download-section">
          <div class="download-card" @click="downloadTemplate">
            <div class="download-icon">
              <i class="bi bi-filetype-csv"></i>
            </div>
            <div class="download-info">
              <h4>Electricity Readings - {{ houseName }}.csv</h4>
              <p>Contains {{ summary.total }} units • Fill in the current_reading column</p>
            </div>
            <button class="btn btn-primary" :disabled="downloading">
              <i class="bi bi-download"></i>
              {{ downloading ? 'Generating...' : 'Download' }}
            </button>
          </div>
        </div>

        <!-- Continue Button -->
        <div class="action-buttons">
          <button class="btn btn-primary btn-lg" @click="currentStep = 2">
            I've filled the readings <i class="bi bi-arrow-right"></i>
          </button>
        </div>
      </div>

      <!-- STEP 2: UPLOAD READINGS -->
      <div v-else-if="currentStep === 2" class="step-card">
        <div class="card-header">
          <i class="bi bi-cloud-upload"></i>
          <h3>Upload Completed Readings CSV</h3>
        </div>

        <!-- Quick Stats -->
        <div class="quick-stats">
          <div class="stat-box">
            <span class="stat-value">{{ summary.total }}</span>
            <span class="stat-label">Total Units</span>
          </div>
          <div class="stat-box">
            <span class="stat-value">{{ summary.occupied }}</span>
            <span class="stat-label">Occupied</span>
          </div>
          <div class="stat-box">
            <span class="stat-value">{{ unitsWithReadings }}</span>
            <span class="stat-label">Have Previous Readings</span>
          </div>
        </div>

        <!-- Upload Area -->
        <div class="upload-container">
          <div 
            class="upload-area" 
            :class="{ 'drag-over': dragOver, 'has-file': uploadedFile }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="handleFileDrop"
          >
            <input 
              type="file" 
              ref="fileInput" 
              accept=".csv"
              @change="handleFileSelect"
              style="display: none;"
            >
            
            <div v-if="!uploadedFile" class="upload-prompt">
              <i class="bi bi-cloud-arrow-up"></i>
              <h4>Drag & drop your CSV file here</h4>
              <p>or</p>
              <button class="btn btn-primary" @click="$refs.fileInput.click()">
                Browse Files
              </button>
              <p class="file-requirements">
                <i class="bi bi-info-circle"></i>
                Only CSV files accepted (Max 10MB)
              </p>
            </div>

            <div v-else class="file-info">
              <i class="bi bi-file-earmark-check text-success"></i>
              <div class="file-details">
                <h5>{{ uploadedFile.name }}</h5>
                <p>{{ (uploadedFile.size / 1024).toFixed(2) }} KB</p>
              </div>
              <button class="btn btn-sm btn-outline-danger" @click="removeFile">
                <i class="bi bi-x"></i>
              </button>
            </div>
          </div>

          <!-- Validation Results -->
          <transition name="fade">
            <div v-if="validationResults" class="validation-results" :class="validationResults.valid ? 'valid' : 'invalid'">
              <div class="validation-header">
                <i :class="validationResults.valid ? 'bi bi-check-circle-fill' : 'bi bi-exclamation-triangle-fill'"></i>
                <h5>{{ validationResults.valid ? 'File is valid!' : 'Validation Errors Found' }}</h5>
              </div>
              
              <div v-if="!validationResults.valid" class="error-list">
                <div v-for="(error, index) in validationResults.errors" :key="index" class="error-item">
                  <i class="bi bi-x-circle"></i>
                  <span>{{ error }}</span>
                </div>
              </div>

              <div v-if="validationResults.stats" class="upload-stats">
                <div class="stat-row">
                  <span>Units in file:</span>
                  <strong>{{ validationResults.stats.total }}</strong>
                </div>
                <div class="stat-row">
                  <span>Readings provided:</span>
                  <strong class="text-success">{{ validationResults.stats.withReadings }}</strong>
                </div>
                <div class="stat-row" v-if="validationResults.stats.missingReadings > 0">
                  <span>Missing readings:</span>
                  <strong class="text-warning">{{ validationResults.stats.missingReadings }}</strong>
                </div>
              </div>
            </div>
          </transition>

          <!-- Preview Uploaded Data -->
          <transition name="fade">
            <div v-if="uploadedFile && validationResults?.valid" class="data-preview">
              <h5>
                <i class="bi bi-table"></i>
                Preview of uploaded data (first 5 rows)
              </h5>
              <div class="preview-scroll">
                <table class="preview-data-table dash-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Tenant</th>
                      <th>Previous</th>
                      <th>Current</th>
                      <th>Consumption (kWh)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in previewData" :key="index">
                      <td>{{ row.unit_number }}</td>
                      <td>{{ row.tenant_name }}</td>
                      <td>{{ row.previous_reading }}</td>
                      <td class="current-reading">{{ row.current_reading || '—' }}</td>
                      <td class="consumption">{{ calculateConsumption(row) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </transition>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button class="btn btn-outline-secondary" @click="currentStep = 1">
            <i class="bi bi-arrow-left"></i> Back to Template
          </button>
          <button 
            class="btn btn-success btn-lg" 
            @click="uploadReadings" 
            :disabled="!uploadedFile || uploading || (validationResults && !validationResults.valid)"
          >
            <i class="bi bi-cloud-upload"></i>
            {{ uploading ? 'Uploading...' : 'Upload Readings' }}
          </button>
        </div>
      </div>

      <!-- SUCCESS MODAL -->
      <transition name="fade">
        <div v-if="showSuccessModal" class="modal-overlay" @click.self="closeSuccessModal">
          <div class="success-modal">
            <div class="success-icon">
              <i class="bi bi-check-circle-fill"></i>
            </div>
            <h3>Upload Successful!</h3>
            <p class="success-message">{{ uploadSummary }}</p>
            
            <div class="success-stats">
              <div class="stat-circle">
                <span class="stat-number">{{ uploadStats.processed }}</span>
                <span class="stat-label">Readings Processed</span>
              </div>
              <div class="stat-circle">
                <span class="stat-number">{{ uploadStats.updated }}</span>
                <span class="stat-label">Units Updated</span>
              </div>
            </div>

            <div class="consumption-summary" v-if="uploadStats.totalConsumption">
              <i class="bi bi-lightning-charge"></i>
              <span>Total Consumption: <strong>{{ uploadStats.totalConsumption }} kWh</strong></span>
            </div>

            <div class="modal-actions">
              <button class="btn btn-primary" @click="uploadAnother">
                <i class="bi bi-plus-circle"></i> Upload Another
              </button>
              <button class="btn btn-outline-secondary" @click="goToUnits">
                <i class="bi bi-house-door"></i> Back to Units
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- LOADING OVERLAY -->
      <transition name="fade">
        <div v-if="uploading" class="loading-overlay">
          <div class="loading-spinner">
            <div class="spinner-border text-light" style="width: 4rem; height: 4rem;"></div>
            <p>Uploading readings...</p>
            <p class="small">Please don't close this window</p>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {

  data() {
    return {
      loading: true,
      error: null,
      units: [],
      houseName: "Property",
      houseLocation: "",
      summary: {
        total: 0,
        occupied: 0,
        vacant: 0
      },
      currentStep: 1,
      downloading: false,
      uploading: false,
      dragOver: false,
      uploadedFile: null,
      validationResults: null,
      showSuccessModal: false,
      uploadSummary: '',
      uploadStats: {
        processed: 0,
        updated: 0,
        totalConsumption: 0
      },
      previewData: []
    };
  },

  computed: {
    houseId() {
      return this.$route.params.house_Id;
    },

    unitsWithReadings() {
      return this.units.filter(u => u.last_meter_reading).length;
    },

    lastReadingDate() {
      const dates = this.units
        .map(u => u.last_reading_date)
        .filter(d => d)
        .sort()
        .reverse();
      
      return dates.length > 0 ? new Date(dates[0]).toLocaleDateString() : null;
    },

    csvPreviewContent() {
      const headers = ['unit_number', 'tenant_name', 'previous_reading', 'current_reading'];
      const rows = this.units.slice(0, 3).map(unit => {
        const tenantName = unit.Tenant ? unit.Tenant.tenant_name : 'Vacant';
        return [
          unit.unit_number || unit.number || unit.unit_no || 'N/A',
          tenantName,
          unit.electricity_meter_readings || 0,
          unit.electricity_meter_readings || 0,
          
          '' // Empty for user to fill
        ].join(',');
      });
      
      return [headers.join(','), ...rows].join('\n');
    }
  },

  mounted() {
     if (this.houseId) {
    this.fetchUnits(); // existing method is fine
  } else {
    console.error("House ID missing from route params!");
  }
},
    
  methods: {
    async fetchUnits() {
      this.loading = true;
      this.error = null;

      try {
        // Single API call to get all units with tenant data
        const res = await apiFetch(`/units/house/${this.houseId}`);
        const rawUnits = res.data || [];

        // Process units and determine status (just like in HouseUnits component)
        this.units = rawUnits.map(unit => {
          const isOccupied = unit.tenant_id || (unit.Tenant && unit.Tenant.tenant_name);
          return {
            ...unit,
            status: isOccupied ? "occupied" : "vacant"
          };
        });

        // Set house name and location from first unit's House data
        if (this.units.length && this.units[0].House) {
          this.houseName = this.units[0].House.name;
          this.houseLocation = this.units[0].House.location;
        }

        // Calculate summary
        const occupied = this.units.filter(u => u.status === "occupied").length;
        this.summary = {
          total: this.units.length,
          occupied,
          vacant: this.units.length - occupied
        };

      } catch (err) {
        console.error("Failed to load units:", err);
        this.error = err.message || "Failed to load property data";
      } finally {
        this.loading = false;
      }
    },

    downloadTemplate() {
      this.downloading = true;
      
      try {
        // Prepare CSV content using the units data
        const rows = [];
        
        // Add headers
        rows.push(['unit_number', 'tenant_name', 'previous_reading', 'current_reading']);
        
        // Add data rows for all units
        this.units.forEach(unit => {
          const tenantName = unit.Tenant ? unit.Tenant.tenant_name : 'Vacant';
          rows.push([
            unit.unit_number || unit.number || unit.unit_no || 'N/A',
            tenantName,
            unit.electricity_meter_readings || 0,
             unit.electricity_meter_readings || 0,
            '' // Empty for user to fill
          ]);
        });

        // Convert to CSV string with proper escaping
        const csvContent = rows.map(row => 
          row.map(cell => {
            // Handle cells that might contain commas
            if (typeof cell === 'string' && cell.includes(',')) {
              return `"${cell}"`;
            }
            return cell;
          }).join(',')
        ).join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Electricity_readings_${this.houseName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show success message
        this.$toast.success('Template downloaded successfully!');

      } catch (err) {
        console.error("Download failed:", err);
        this.$toast.error('Failed to download template');
      } finally {
        this.downloading = false;
      }
    },

    handleFileDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.processFile(file);
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },

    async processFile(file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.$toast.error('Invalid file type. Please upload a CSV file.');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        this.$toast.error('File too large. Maximum size is 10MB.');
        return;
      }

      this.uploadedFile = file;
      await this.validateCSV(file);
    },

    async validateCSV(file) {
      this.validationResults = null;
      this.previewData = [];
      
      try {
        const text = await file.text();
        const lines = text.split('\n').map(line => line.trim()).filter(line => line);
        
        if (lines.length < 2) {
          this.validationResults = {
            valid: false,
            errors: ['File is empty or invalid'],
            stats: { total: 0, withReadings: 0, missingReadings: 0 }
          };
          return;
        }

        // Parse headers
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const requiredHeaders = ['unit_number', 'tenant_name', 'previous_reading', 'current_reading'];
        
        const errors = [];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          errors.push(`Missing required columns: ${missingHeaders.join(', ')}`);
        }

        // Parse data rows
        const data = [];
        const stats = {
          total: lines.length - 1,
          withReadings: 0,
          missingReadings: 0
        };

        // Create a map of existing units for validation
        const unitMap = new Map(
          this.units.map(unit => [
            unit.unit_number || unit.number || unit.unit_no, 
            unit
          ])
        );

        for (let i = 1; i < lines.length; i++) {
          const values = this.parseCSVRow(lines[i]);
          
          if (values.length >= 4) {
            const row = {
              unit_number: values[0].trim(),
              tenant_name: values[1].trim(),
              previous_reading: values[2].trim(),
              current_reading: values[3].trim()
            };
            
            data.push(row);

            // Check if unit exists in this property
            if (!unitMap.has(row.unit_number)) {
              if (errors.length < 5) {
                errors.push(`Row ${i}: Unit "${row.unit_number}" does not exist in this property`);
              }
            }

            // Validate current reading
            if (row.current_reading && !isNaN(row.current_reading) && row.current_reading !== '') {
              stats.withReadings++;
            } else {
              stats.missingReadings++;
              if (errors.length < 5 && row.unit_number) {
                errors.push(`Row ${i}: Missing or invalid current reading for unit ${row.unit_number}`);
              }
            }
          } else {
            errors.push(`Row ${i}: Invalid format (expected 4 columns, got ${values.length})`);
          }
        }

        // Prepare preview data (first 5 rows)
        this.previewData = data.slice(0, 5);

        this.validationResults = {
          valid: errors.length === 0,
          errors: errors.slice(0, 5),
          stats
        };

      } catch (err) {
        console.error("Validation error:", err);
        this.validationResults = {
          valid: false,
          errors: ['Failed to parse CSV file. Please check the format.'],
          stats: { total: 0, withReadings: 0, missingReadings: 0 }
        };
      }
    },

    parseCSVRow(row) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        
        if (char === '"' && (i === 0 || row[i-1] !== '\\')) {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      
      values.push(current); // Add the last value
      return values;
    },

    calculateConsumption(row) {
      if (!row.current_reading || row.current_reading === '') return '—';
      const prev = parseFloat(row.previous_reading) || 0;
      const curr = parseFloat(row.current_reading) || 0;
      if (curr < prev) return 'Invalid (reset)';
      return (curr - prev).toFixed(1) + ' m³';
    },

    removeFile() {
      this.uploadedFile = null;
      this.validationResults = null;
      this.previewData = [];
      this.$refs.fileInput.value = '';
    },

  
async uploadReadings() {
  if (!this.uploadedFile || !this.houseId) {
    this.$toast.error("Please select a house and CSV file.");
    return;
  }

  this.uploading = true;

  try {
    const formData = new FormData();
    formData.append("file", this.uploadedFile);
    formData.append("houseId", this.houseId);
    console.log("sent house id", this.houseId)

    // Do NOT set Content-Type; browser will handle multipart boundary
    const response = await apiFetch("/electricity-meter-readings/upload", {
      method: "POST",
      body: formData,
      headers: {} // keeps apiFetch from forcing JSON
    });

    // Calculate total consumption from previewData (optional)
    let totalConsumption = 0;
    this.previewData.forEach(row => {
      const prev = parseFloat(row.previous_reading) || 0;
      const curr = parseFloat(row.current_reading) || 0;
      if (curr > prev) {
        totalConsumption += (curr - prev);
      }
    });

    this.uploadStats = {
      processed: response.created || 0,
      rejected: response.rejected ?.length || 0,
      totalConsumption: totalConsumption.toFixed(1)
    };

    this.uploadSummary =
      response.message ||
      `Successfully uploaded ${this.uploadStats.processed} meter readings.`;

    this.showSuccessModal = true;

    // Refresh units to show updated readings
    await this.fetchUnits();

  } catch (err) {
    console.error("Upload failed:", err);
    this.$toast.error(err.message || "Failed to upload readings. Please try again.");
  } finally {
    this.uploading = false;
  }
},

    goBack() {
      this.$router.go(-1);
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
    },

    uploadAnother() {
      this.showSuccessModal = false;
      this.currentStep = 2;
      this.uploadedFile = null;
      this.validationResults = null;
      this.previewData = [];
    },

    goToUnits() {
      this.$router.push(`/house/${this.houseId}/units`);
    }
  }
};
</script>

<style scoped>
/* Copy all the styles from the water component - they remain exactly the same */
.electricity-meter-container {
  background: var(--dash-bg);
  min-height: 100vh;
}

.meter-content {
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: var(--dash-fs-display);
  font-weight: 700;
  color: var(--dash-ink);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-title i {
  color: var(--dash-warning);
  font-size: 1.05em;
}

.page-subtitle {
  color: var(--dash-muted);
  font-size: 1.1rem;
  margin-bottom: 0;
}

/* Property Info Card - Different gradient for electricity */
.property-info-card {
  background: var(--dash-primary);
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 30px;
  color: white;
  box-shadow: var(--dash-shadow);
}

.property-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: var(--dash-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
}

.property-details {
  flex: 1;
}

.property-details h2 {
  margin: 0 0 10px 0;
  font-size: var(--dash-fs-h1);
  font-weight: 600;
}

.property-location {
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0.9;
}

.property-stats {
  display: flex;
  gap: 15px;
}

.stat-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: var(--dash-pill);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Rest of the styles are identical to water component */
.steps-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
  padding: 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--dash-line);
  color: var(--dash-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: var(--dash-warning);
  color: white;
  transform: scale(1.1);
  box-shadow: var(--dash-shadow);
}

.step.completed .step-number {
  background: var(--dash-success);
  color: white;
}

.step-label {
  font-size: 0.9rem;
  color: var(--dash-muted);
  font-weight: 500;
}

.step.active .step-label {
  color: var(--dash-warning);
  font-weight: 600;
}

.step-connector {
  width: 60px;
  height: 2px;
  background: var(--dash-line);
  margin: 0 10px;
}

/* Step Cards */
.step-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
}

.card-header i {
  font-size: 1.05em;
  color: var(--dash-warning);
}

.card-header h3 {
  margin: 0;
  color: var(--dash-ink);
}

/* Template Info */
.template-info {
  display: flex;
  gap: 15px;
  padding: 20px;
  background: var(--dash-warning-soft);
  border-radius: var(--dash-radius);
  margin-bottom: 30px;
  border-left: 4px solid var(--dash-warning);
}

.info-icon i {
  font-size: 1.5rem;
  color: var(--dash-warning);
}

.info-text {
  flex: 1;
  color: var(--dash-ink);
  line-height: 1.6;
}

.highlight {
  background: var(--dash-warning);
  color: white;
  padding: 2px 8px;
  border-radius: var(--dash-radius-lg);
  font-weight: 600;
}

/* Template Preview */
.template-preview {
  margin-bottom: 30px;
}

.template-preview h5 {
  color: var(--dash-ink);
  margin-bottom: 15px;
}

.preview-csv {
  background: var(--dash-ink);
  border-radius: var(--dash-radius);
  padding: 20px;
  margin-bottom: 10px;
  overflow-x: auto;
}

.csv-content {
  color: var(--dash-line-strong);
  font-family: var(--dash-font-mono);
  font-size: 0.9rem;
  margin: 0;
  white-space: pre;
}

.preview-note {
  color: var(--dash-muted);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Download Section */
.download-section {
  margin-bottom: 30px;
}

.download-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
  border: 2px solid var(--dash-line);
  transition: all 0.3s ease;
  cursor: pointer;
}

.download-card:hover {
  border-color: var(--dash-warning);
  background: white;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.download-icon i {
  font-size: 2.25rem;
  color: var(--dash-success);
}

.download-info {
  flex: 1;
}

.download-info h4 {
  margin: 0 0 5px 0;
  color: var(--dash-ink);
}

.download-info p {
  margin: 0;
  color: var(--dash-muted);
  font-size: 0.9rem;
}

/* Quick Stats */
.quick-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 30px;
}

.stat-box {
  text-align: center;
  padding: 20px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
}

.stat-value {
  display: block;
  font-size: var(--dash-fs-figure);
  font-weight: 700;
  color: var(--dash-ink);
  line-height: 1;
  margin-bottom: 5px;
}

.stat-label {
  color: var(--dash-muted);
  font-size: 0.9rem;
}

/* Upload Area */
.upload-container {
  margin-bottom: 30px;
}

.upload-area {
  border: 3px dashed var(--dash-line-strong);
  border-radius: var(--dash-radius-lg);
  padding: 40px;
  text-align: center;
  transition: all 0.3s ease;
  background: var(--dash-surface-2);
  cursor: pointer;
  margin-bottom: 20px;
}

.upload-area.drag-over {
  border-color: var(--dash-warning);
  background: rgba(217, 119, 6, 0.05);
  transform: scale(1.02);
}

.upload-area.has-file {
  border-color: var(--dash-success);
  background: rgba(5, 150, 105, 0.05);
}

.upload-prompt i {
  font-size: 2.25rem;
  color: var(--dash-muted);
  margin-bottom: 20px;
}

.upload-prompt h4 {
  color: var(--dash-ink);
  margin-bottom: 10px;
}

.upload-prompt p {
  color: var(--dash-muted);
  margin: 10px 0;
}

.file-requirements {
  font-size: 0.85rem;
  color: var(--dash-faint);
  margin-top: 20px;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.file-info i {
  font-size: 2rem;
}

.file-details {
  flex: 1;
  text-align: left;
}

.file-details h5 {
  margin: 0 0 5px 0;
  color: var(--dash-ink);
}

.file-details p {
  margin: 0;
  color: var(--dash-muted);
}

/* Validation Results */
.validation-results {
  padding: 20px;
  border-radius: var(--dash-radius);
  margin-bottom: 20px;
}

.validation-results.valid {
  background: rgba(5, 150, 105, 0.1);
  border-left: 4px solid var(--dash-success);
}

.validation-results.invalid {
  background: rgba(220, 38, 38, 0.1);
  border-left: 4px solid var(--dash-danger);
}

.validation-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.validation-header i {
  font-size: 1.5rem;
}

.valid .validation-header i {
  color: var(--dash-success);
}

.invalid .validation-header i {
  color: var(--dash-danger);
}

.validation-header h5 {
  margin: 0;
  color: var(--dash-ink);
}

.error-list {
  margin-bottom: 15px;
}

.error-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  margin-bottom: 5px;
  background: white;
  border-radius: var(--dash-radius-xs);
  color: var(--dash-danger);
  font-size: 0.9rem;
}

.upload-stats {
  padding: 15px;
  background: white;
  border-radius: var(--dash-radius-xs);
}

.stat-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--dash-line);
}

.stat-row:last-child {
  border-bottom: none;
}

/* Data Preview */
.data-preview {
  margin-top: 20px;
  padding: 20px;
  background: white;
  border-radius: var(--dash-radius);
  border: 1px solid var(--dash-line);
}

.data-preview h5 {
  margin: 0 0 15px 0;
  color: var(--dash-ink);
  display: flex;
  align-items: center;
  gap: 10px;
}

.preview-scroll {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--dash-line);
  border-radius: var(--dash-radius-xs);
}

.preview-data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.preview-data-table th {
  background: var(--dash-surface-2);
}

.preview-data-table .current-reading {
  font-weight: 600;
  color: var(--dash-success);
}

.preview-data-table .consumption {
  color: var(--dash-warning);
  font-weight: 500;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.action-buttons .btn {
  padding: 12px 30px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Success Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.success-modal {
  background: white;
  border-radius: var(--dash-pill);
  padding: 40px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.success-icon {
  width: 80px;
  height: 80px;
  background: var(--dash-success);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
}

.success-icon i {
  font-size: 2rem;
  color: white;
}

.success-modal h3 {
  color: var(--dash-ink);
  margin-bottom: 15px;
  font-size: var(--dash-fs-h1);
}

.success-message {
  color: var(--dash-muted);
  margin-bottom: 30px;
  font-size: 1.1rem;
}

.success-stats {
  display: flex;
  gap: 30px;
  justify-content: center;
  margin-bottom: 30px;
}

.stat-circle {
  text-align: center;
}

.stat-circle .stat-number {
  display: block;
  font-size: var(--dash-fs-figure);
  font-weight: 700;
  color: var(--dash-success);
  line-height: 1;
  margin-bottom: 5px;
}

.stat-circle .stat-label {
  color: var(--dash-muted);
  font-size: 0.9rem;
}

.consumption-summary {
  background: var(--dash-warning-soft);
  padding: 15px;
  border-radius: var(--dash-radius);
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--dash-warning-ink);
  font-size: 1.1rem;
}

.consumption-summary i {
  font-size: 1.5rem;
}

.modal-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.modal-actions .btn {
  padding: 12px 25px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(5px);
}

.loading-spinner {
  text-align: center;
  color: white;
}

.loading-spinner p {
  margin-top: 15px;
  font-size: 1.1rem;
}

.loading-spinner .small {
  font-size: 0.9rem;
  opacity: 0.8;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px;
  background: white;
  border-radius: var(--dash-radius-lg);
}

/* Error State */
.error-state {
  text-align: center;
  padding: 60px;
  background: white;
  border-radius: var(--dash-radius-lg);
}

.error-state i {
  font-size: 2.25rem;
  color: var(--dash-danger);
  margin-bottom: 20px;
}

.error-state h4 {
  color: var(--dash-ink);
  margin-bottom: 10px;
}

.error-state p {
  color: var(--dash-muted);
  margin-bottom: 20px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .meter-content {
    padding: 20px;
  }

  .page-title {
    font-size: var(--dash-fs-h1);
  }

  .property-info-card {
    flex-direction: column;
    text-align: center;
  }

  .property-stats {
    flex-wrap: wrap;
    justify-content: center;
  }

  .steps-container {
    flex-direction: column;
    gap: 15px;
  }

  .step-connector {
    width: 2px;
    height: 20px;
  }

  .quick-stats {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    flex-direction: column;
    gap: 10px;
  }

  .action-buttons .btn {
    width: 100%;
  }

  .modal-actions {
    flex-direction: column;
  }

  .download-card {
    flex-direction: column;
    text-align: center;
  }

  .file-info {
    flex-direction: column;
    text-align: center;
  }
}
</style>